import styled from 'styled-components'
import moment from 'moment'

// ==========================================
// Shared Styled Components
// ==========================================

/**
 * CardGroup - Container for a label/value pair
 * Used in DetailView and DetailViewLines
 */
export const CardGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: ${({ $length }) => $length || 'auto'};
    flex: ${({ $shrink }) => $shrink === 0 ? '0 0 auto' : '1 1 auto'};
`

/**
 * CardLabel - Label text for a field group
 * Used in DetailView and DetailViewLines
 */
export const CardLabel = styled.span`
    font-size: var(--font-XXS);
    color: ${({ theme }) => theme?.font?.label || '#515559'};
    text-transform: uppercase;
    letter-spacing: 0.5px;
`

/**
 * CardValue - Value text for a field group
 * Used in DetailView and DetailViewLines
 */
export const CardValue = styled.span`
    font-size: ${({ $size }) => $size === 'title' ? 'var(--font-M)' : 'var(--font-XS)'};
    font-weight: ${({ $size }) => $size === 'title' ? '600' : '400'};
    color: ${({ theme }) => theme?.font?.color || '#333'};
`

// ==========================================
// Shared Utility Functions
// ==========================================

/**
 * Format a value based on the format configuration
 * 
 * @param {*} value - The value to format
 * @param {string|object} format - Format type ('date', 'amount') or boolean config
 * @param {object} data - The full data object (for context)
 * @param {function} t - Translation function
 * @returns {string} Formatted value
 */
export const formatValue = (value, format, data, t, field) => {
    if (!format) return value ?? '-'
    
    if (format === 'date') {
        return value ? moment(value).format('DD.MM.YYYY') : '-'
    }
    
    if (format === 'label') {
        return t(field)
    }

    if (format === 'amount') {
        const amount = parseFloat(value) || 0
        return amount.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
    
    if (typeof format === 'object' && format.type === 'boolean') {
        return value ? t(format.true) : t(format.false)
    }
    
    return value ?? ' - ' 
}

/**
 * Merge multiple fields from data into a display string
 * 
 * @param {string[]} mergeFields - Array of field names to merge
 * @param {object} data - The data object to extract values from
 * @param {object} formats - Format configuration per field
 * @param {function} t - Translation function
 * @returns {string} Merged display string
 */
export const mergeFields = (mergeFields, data, formats, t) => {
    if (!mergeFields || !data) return '-'
    
    const values = mergeFields.map(field => {
        const value = data[field]
        const format = formats?.[field]
        return formatValue(value, format, data, t, field)
    })
    
    // Filter out undefined/null/empty values and join with space
    return values.filter(v => v !== undefined && v !== null && v !== '').join(' ')
}

/**
 * Render a group of fields as CardGroup components
 * 
 * @param {array} groups - Array of group configurations
 * @param {object} item - The data item to display
 * @param {function} t - Translation function
 * @returns {JSX.Element[]} Array of CardGroup elements
 */
export const renderCardGroups = (groups, item, t) => {
    return groups.map((group, groupIndex) => {
        const displayValue = mergeFields(group.mergeFields, item, group.formats, t)
        return (
            <CardGroup 
                key={groupIndex} 
                $length={group.width}
                $shrink={group.shrink}
            >
                <CardLabel>{t(group.label)}</CardLabel>
                <CardValue $size={group.scale}>{displayValue}</CardValue>
            </CardGroup>
        )
    })
}
